import styled from 'styled-components';

import { ActionStatus, WorkflowStatus } from 'models/Workflow';
import { colors } from 'styles/theme/colors';

// One status maps to exactly one presentation everywhere it appears (UX guide §5).
export type Tone = 'neutral' | 'info' | 'running' | 'review' | 'warning' | 'success' | 'danger';

// `background`/`color` style the tinted/solid pills; `accent` is the single solid color that
// represents the tone in non-pill contexts (legend swatches, the progress-bar segments) — the
// standard olio palette hue for that status. It is never the pill's text color, which for the
// solid `review` pill is white (the bug that made "Ready for Review" swatches invisible).
export const TONE_STYLES: Record<Tone, { background: string; color: string; accent: string }> = {
  neutral: { background: colors.black5, color: colors.black50, accent: colors.black25 },
  info: { background: colors.blue10, color: colors.blue100, accent: colors.blue100 },
  // AI/automated work in flight — the purple accent shared with the running spinner.
  running: { background: colors.purple10, color: colors.purple100, accent: colors.purple100 },
  // Ready for a person to act — a light blue tint pill (accent stays solid blue for swatches).
  review: { background: colors.blue10, color: colors.blue100, accent: colors.blue100 },
  // Needs attention — the standard olio secondaryOrange (the swatch/segment accent and pill
  // tint); the pill text is a darker shade of it for readable contrast on the light tint.
  warning: { background: colors.orange10, color: colors.orangeHover, accent: colors.orange100 },
  success: { background: colors.green10, color: colors.green100, accent: colors.green100 },
  danger: { background: colors.red10, color: colors.red100, accent: colors.red100 },
};

const WORKFLOW_STATUS_DISPLAY: Record<WorkflowStatus, { label: string; tone: Tone }> = {
  [WorkflowStatus.RUNNING]: { label: 'In Progress', tone: 'info' },
  [WorkflowStatus.ACTION_REQUIRED]: { label: 'Needs Attention', tone: 'warning' },
  [WorkflowStatus.COMPLETED]: { label: 'Completed', tone: 'success' },
  [WorkflowStatus.CANCELED]: { label: 'Canceled', tone: 'neutral' },
};

const ACTION_STATUS_DISPLAY: Record<ActionStatus, { label: string; tone: Tone }> = {
  [ActionStatus.PENDING]: { label: 'Pending', tone: 'neutral' },
  [ActionStatus.RUNNING]: { label: 'Running', tone: 'running' },
  [ActionStatus.ACTION_REQUIRED]: { label: 'Ready for Review', tone: 'review' },
  [ActionStatus.FLAGGED]: { label: 'Flagged', tone: 'warning' },
  [ActionStatus.COMPLETED]: { label: 'Complete', tone: 'success' },
  [ActionStatus.ERRORED]: { label: 'Error', tone: 'danger' },
  [ActionStatus.CANCELED]: { label: 'Canceled', tone: 'neutral' },
  [ActionStatus.SKIPPED]: { label: 'Not Applicable', tone: 'neutral' },
};

// `archived` reads the pair rather than a status of its own: archiving IS the cancel, and the
// stamp records that a person chose it, so a run the engine canceled on its own (a confirmed
// duplicate) must keep saying Canceled (OLIO-2599).
export function workflowStatusDisplay(status: WorkflowStatus, archived = false) {
  if (archived && status === WorkflowStatus.CANCELED) return { label: 'Archived', tone: 'neutral' as Tone };

  return WORKFLOW_STATUS_DISPLAY[status] ?? { label: status, tone: 'neutral' as Tone };
}

export function actionStatusDisplay(status: ActionStatus) {
  return ACTION_STATUS_DISPLAY[status] ?? { label: status, tone: 'neutral' as Tone };
}

export function WorkflowStatusBadge({ status, archived = false }: { status: WorkflowStatus; archived?: boolean }) {
  const display = workflowStatusDisplay(status, archived);
  return (
    <Pill $tone={display.tone} data-testid='workflow-status'>
      {display.label}
    </Pill>
  );
}

export function ActionStatusBadge({ status }: { status: ActionStatus }) {
  const display = actionStatusDisplay(status);
  return (
    <Pill $tone={display.tone} data-testid='action-status'>
      {display.label}
    </Pill>
  );
}

export const Pill = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border-radius: 24px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].color};
`;
